import { getStoreDefinition } from '@/composables/StoreDefinition'
import { DatabaseView } from '@/enums/DatabaseView'
import { EndPoint } from '@/enums/EndPoint'
import { SqlComparison } from '@/enums/SqlComparison'
import { SqlOperator } from '@/enums/SqlOperator'
import { SqlOrder } from '@/enums/SqlOrder'
import { TableStoreOptions } from '@/enums/TableStoreOptions'
import type { IDevicesStore } from '@/interfaces/api/IDevicesStore'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import type { TableRecord } from '@/models/TableRecord'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { useSettingsStore } from '../ui/SettingsStore'
import { useAPIStore } from './ApiStore'

let _store: IDevicesStore

export const useDevicesStore = () => {
  if (_store) {
    return _store
  }
  const option = TableStoreOptions.Devices
  const useStore = defineStore(option.id, getStoreDefinition(option), {
    persist: {
      storage: localStorage,
      pick: ['itemsPerPage']
    }
  })
  _store = useStore() as unknown as IDevicesStore
  _extend(_store)
  return _store
}
const _extend = (store: IDevicesStore) => {
  const apiStore = useAPIStore()
  const settingsStore = useSettingsStore()

  // Override the default table with a view.
  store.useView(DatabaseView.DeviceWithDetail)

  // Before deleting a device, disconnect its related Event records.
  // Don't allow deleting devices with upcoming Events.
  store.beforeDelete = async (item: TableRecord): Promise<void> => {
    const query: IQueryOptions = {
      where: [
        {
          field: 'deviceId',
          comparison: SqlComparison.Equal,
          value1: item.id,
          operator: SqlOperator.And
        },
        {
          field: 'dtStart',
          comparison: SqlComparison.LessThan,
          value1: new Date().toISOString()
        }
      ],
      update: {
        deviceId: null
      }
    }
    await apiStore.updateMany(EndPoint.Events, query, true)
  }

  store.getDeviceOptions = async (): Promise<Array<ISimpleOption>> => {
    const query: IQueryOptions = {
      isDistinct: true,
      fields: ['id', 'name', 'serialNumber', 'location'],
      orderBy: [
        { field: 'name', order: SqlOrder.ASC },
        { field: 'serialNumber', order: SqlOrder.ASC }
      ]
    }
    const response = await apiStore.getMany(EndPoint.Devices, query)
    let options: Array<ISimpleOption> = []
    if (!response.error) {
      const supervisors = response.data?.results || []
      options = supervisors.map((item) => {
        return { title: `${item.name} (${item.serialNumber})`, value: item.id! }
      })
    }
    return options
  }

  store.getDeviceExpirations = async () => {
    const calExpires = dayjs().subtract(
      settingsStore.globals.devices.daysToExpireCalibration -
        settingsStore.globals.devices.daysUntilDueCalibration -
        1,
      'day'
    )
    const planExpires = dayjs().add(settingsStore.globals.devices.daysUntilDuePlan - 1, 'day')
    const queryOptions: IQueryOptions = {
      where: [
        {
          comparison: SqlComparison.LessThan,
          field: 'lastCalibration',
          value1: calExpires.toISOString(),
          operator: SqlOperator.Or
        },
        {
          comparison: SqlComparison.LessThan,
          field: 'planExpiration',
          value1: planExpires.toISOString()
        }
      ],
      fields: ['id', 'serialNumber', 'lastCalibration', 'planExpiration']
    }
    return apiStore.getMany(EndPoint.Devices, queryOptions)
  }
}
